import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Calendar,
  Users,
  Eye,
  TrendingUp,
  ChevronDown,
  RefreshCw,
  AlertCircle
} from 'lucide-react'
import { useAuth } from '../../App'
import { mockCampaigns, statusColors, formatCurrency, formatNumber } from '../../data/mockData'

function BrandCampaigns() {
  const { user } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [viewMode, setViewMode] = useState('grid')
  const [campaigns, setCampaigns] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchCampaigns()
  }, [user])

  const fetchCampaigns = async () => {
    setIsLoading(true)
    setError('')

    try {
      const response = await fetch(`/api/campaigns?brandUserId=${user?.id}`)
      const data = await response.json()

      // Combine real campaigns with mock campaigns for demo
      const realCampaigns = (data.campaigns || []).map(c => ({
        ...c,
        startDate: c.createdAt?.split('T')[0] || new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        deliverablesCount: 0,
        completedDeliverables: 0,
        reach: 0,
        engagement: 0,
        isReal: true,
      }))

      const allCampaigns = [...realCampaigns, ...mockCampaigns.map(c => ({ ...c, isDemo: true }))]
      setCampaigns(allCampaigns)
    } catch (err) {
      console.error('Failed to fetch campaigns:', err)
      // Use mock data as fallback
      setCampaigns(mockCampaigns.map(c => ({ ...c, isDemo: true })))
    } finally {
      setIsLoading(false)
    }
  }

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter
    return matchesSearch && matchesStatus
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-hf-teal-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
          <p className="text-gray-500">Manage and track all your influencer campaigns</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchCampaigns} className="btn-secondary">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <Link to="/brand/campaigns/new" className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Link>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {/* Filters Bar */}
      <div className="card !p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-hf-teal-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-hf-teal-500 bg-white"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="matching">Matching</option>
              <option value="in_progress">In Progress</option>
              <option value="content_review">Content Review</option>
              <option value="completed">Completed</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* View Toggle */}
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-4 py-2 text-sm font-medium ${
                viewMode === 'grid' ? 'bg-hf-teal-50 text-hf-teal-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 text-sm font-medium border-l border-gray-200 ${
                viewMode === 'list' ? 'bg-hf-teal-50 text-hf-teal-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              List
            </button>
          </div>
        </div>
      </div>

      {/* Campaign Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCampaigns.map((campaign) => {
            const status = statusColors[campaign.status] || statusColors.draft
            const progress = campaign.deliverablesCount > 0
              ? Math.round((campaign.completedDeliverables / campaign.deliverablesCount) * 100)
              : 0

            return (
              <Link
                key={campaign.id}
                to={`/brand/campaigns/${campaign.id}`}
                className="card hover:shadow-lg transition-shadow group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className={`badge ${status.bg} ${status.text}`}>
                      {status.label}
                    </span>
                    {campaign.isReal && (
                      <span className="badge bg-green-100 text-green-700 text-xs">Live</span>
                    )}
                    {campaign.isDemo && (
                      <span className="badge bg-purple-100 text-purple-700 text-xs">Demo</span>
                    )}
                  </div>
                  <button className="p-1 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-hf-teal-600 transition-colors">
                  {campaign.name}
                </h3>

                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <Calendar className="w-4 h-4 mr-1.5" />
                  {new Date(campaign.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} -
                  {new Date(campaign.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-500">Progress</span>
                    <span className="font-medium text-gray-900">{progress}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-hf-teal-500 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-100">
                  <div className="text-center">
                    <div className="flex items-center justify-center text-gray-400 mb-1">
                      <Users className="w-4 h-4" />
                    </div>
                    <p className="text-sm font-medium text-gray-900">{campaign.creatorsCount}</p>
                    <p className="text-xs text-gray-500">Creators</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center text-gray-400 mb-1">
                      <Eye className="w-4 h-4" />
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {campaign.reach > 0 ? formatNumber(campaign.reach) : '-'}
                    </p>
                    <p className="text-xs text-gray-500">Reach</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center text-gray-400 mb-1">
                      <TrendingUp className="w-4 h-4" />
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {campaign.engagement > 0 ? `${campaign.engagement}%` : '-'}
                    </p>
                    <p className="text-xs text-gray-500">Engagement</p>
                  </div>
                </div>

                {/* Budget */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-sm text-gray-500">Budget</span>
                  <span className="font-medium text-gray-900">{formatCurrency(campaign.budget)}</span>
                </div>
              </Link>
            )
          })}

          {/* Create New Campaign Card */}
          <Link
            to="/brand/campaigns/new"
            className="card border-2 border-dashed border-gray-200 hover:border-hf-teal-300 hover:bg-hf-teal-50/50 transition-colors flex flex-col items-center justify-center min-h-[300px] group"
          >
            <div className="w-12 h-12 bg-gray-100 group-hover:bg-hf-teal-100 rounded-xl flex items-center justify-center mb-4 transition-colors">
              <Plus className="w-6 h-6 text-gray-400 group-hover:text-hf-teal-600 transition-colors" />
            </div>
            <p className="font-medium text-gray-600 group-hover:text-hf-teal-700 transition-colors">Create New Campaign</p>
            <p className="text-sm text-gray-400 mt-1">Start with a creative brief</p>
          </Link>
        </div>
      ) : (
        /* List View */
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Campaign</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Timeline</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Creators</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Budget</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Reach</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredCampaigns.map((campaign) => {
                const status = statusColors[campaign.status] || statusColors.draft

                return (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <Link to={`/brand/campaigns/${campaign.id}`} className="font-medium text-gray-900 hover:text-hf-teal-600">
                          {campaign.name}
                        </Link>
                        {campaign.isReal && (
                          <span className="badge bg-green-100 text-green-700 text-xs">Live</span>
                        )}
                        {campaign.isDemo && (
                          <span className="badge bg-purple-100 text-purple-700 text-xs">Demo</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`badge ${status.bg} ${status.text}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-500">
                      {new Date(campaign.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} -
                      {new Date(campaign.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="py-4 px-4 text-gray-600">{campaign.creatorsCount}</td>
                    <td className="py-4 px-4 font-medium text-gray-900">{formatCurrency(campaign.budget)}</td>
                    <td className="py-4 px-4 text-gray-600">
                      {campaign.reach > 0 ? formatNumber(campaign.reach) : '-'}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <Link
                        to={`/brand/campaigns/${campaign.id}`}
                        className="text-hf-teal-600 hover:text-hf-teal-700 text-sm font-medium"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {filteredCampaigns.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns found</h3>
          <p className="text-gray-500 mb-4">Try adjusting your search or filter criteria</p>
          <Link to="/brand/campaigns/new" className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Create Campaign
          </Link>
        </div>
      )}
    </div>
  )
}

export default BrandCampaigns
